import { Card, Col, DatePicker, Divider, Empty, Form, Row, Select, Spin, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsSunburst from 'highcharts/modules/sunburst';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { getLeadInterestReport } from '../../services/reportService';

// Initialize the sunburst module
HighchartsSunburst(Highcharts);

const { Title } = Typography;
const { Option } = Select;

// --- Type Definitions for Report Data ---
interface Reason {
  reason: string;
  total: number;
  persentase: number;
}

interface SummaryReasons {
  minat_tidak_pesan: Reason[];
  tidak_minat: Reason[];
}

interface ReportDataItem {
  property_area_name: string;
  total_leads: number;
  data_lead: {
    call_in: {
      total: number;
    };
    minat: {
      total: number;
      describe: {
        pesan: { total: number };
        proses: { total: number };
        tidak_pesan: { total: number };
      };
    };
    tidak_minat: {
      total: number;
    };
  };
  summary_reasons: SummaryReasons;
}
// --- End of Type Definitions ---

const EvaluasiPage = () => {
  const { user, hasPermission } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  
  // State untuk data mentah dari API
  const [rawReportData, setRawReportData] = useState<ReportDataItem[]>([]);
  
  // State untuk data yang akan ditampilkan di chart
  const [chartDisplayData, setChartDisplayData] = useState<any>(null);
  
  // State untuk tabel alasan
  const [alasanTidakMinat, setAlasanTidakMinat] = useState<Reason[]>([]);
  const [alasanTidakPesan, setAlasanTidakPesan] = useState<Reason[]>([]);

  const [filters, setFilters] = useState<{
    property_area_id?: string;
    date_range: [dayjs.Dayjs, dayjs.Dayjs];
  }>({
    property_area_id: undefined,
    date_range: [dayjs().startOf('year'), dayjs().endOf('month')],
  });

  const isManager = hasPermission('lead.property');

  useEffect(() => {
    if (isManager) {
      const fetchPropertyAreas = async () => {
        try {
          const response = await api.get('/property-areas/role');
          setPropertyAreas(response.data.data);
        } catch (error) {
          console.error("Failed to fetch property areas", error);
        }
      };
      fetchPropertyAreas();
    }
  }, [isManager]);

  // --- Fetch Data Effect ---
  useEffect(() => {
    const fetchReportData = async () => {
      // Guard clause: Do not fetch until user data is available.
      if (!user) {
        return;
      }

      setLoading(true);
      setRawReportData([]);
      try {
        let params: any = {};
        if (filters.date_range && filters.date_range[0] && filters.date_range[1]) {
          params.start_date = filters.date_range[0].format('YYYY-MM-DD');
          params.end_date = filters.date_range[1].format('YYYY-MM-DD');
        }

        // For managers, if no specific area is selected, we don't pass any ID to fetch all areas.
        // For non-managers, we must pass ALL their assigned area IDs.
        if (isManager) {
          if (filters.property_area_id) {
            params.property_area_ids = filters.property_area_id;
          }
        } else if (user.user_property_areas && user.user_property_areas.length > 0) {
          const accessibleAreaIds = user.user_property_areas.map(upa => upa.property_area_id);
          params.property_area_ids = accessibleAreaIds.join(',');
        } else {
          // If a non-manager has no property area, don't fetch.
          setLoading(false);
          return;
        }

        const reportRes = await getLeadInterestReport(params);
        setRawReportData(reportRes.data.data || []);

      } catch (error) {
        console.error("Failed to fetch report data", error);
        setRawReportData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [filters, isManager, user]);

  // --- Process Data for Chart and Tables ---
  useEffect(() => {
    if (!rawReportData || rawReportData.length === 0) {
      setChartDisplayData(null);
      setAlasanTidakMinat([]);
      setAlasanTidakPesan([]);
      return;
    }

    let dataForChart: any;
    let tidakMinatReasons: Reason[] = [];
    let tidakPesanReasons: Reason[] = [];

    // Check if a specific property area is filtered
    const isFiltered = isManager && filters.property_area_id;

    if (isFiltered) {
      // --- FILTERED VIEW ---
      const selectedArea = propertyAreas.find(pa => pa.id === filters.property_area_id);
      const singleData = selectedArea ? rawReportData.find(d => d.property_area_name === selectedArea.name) : undefined;
      
      if (singleData) {
        dataForChart = singleData;
        tidakMinatReasons = singleData.summary_reasons?.tidak_minat || [];
        tidakPesanReasons = singleData.summary_reasons?.minat_tidak_pesan || [];
      } else {
        // Handle case where filter is set but no data matches
        dataForChart = null;
        tidakMinatReasons = [];
        tidakPesanReasons = [];
      }
    } else {
      // --- AGGREGATED VIEW (Default for Manager without filter, or for Non-Manager) ---
      dataForChart = {
        property_area_name: "Semua Property Area",
        data_lead: {
          call_in: { total: 0 },
          minat: { total: 0, describe: { pesan: { total: 0 }, proses: { total: 0 }, tidak_pesan: { total: 0 } } },
          tidak_minat: { total: 0 }
        }
      };

      const reasonMapTidakMinat = new Map<string, number>();
      const reasonMapTidakPesan = new Map<string, number>();

      rawReportData.forEach((item) => {
        // Aggregate chart data
        dataForChart.data_lead.call_in.total += item.data_lead.call_in?.total || 0;
        dataForChart.data_lead.minat.total += item.data_lead.minat.total;
        dataForChart.data_lead.tidak_minat.total += item.data_lead.tidak_minat.total;
        dataForChart.data_lead.minat.describe.pesan.total += item.data_lead.minat.describe.pesan.total;
        dataForChart.data_lead.minat.describe.proses.total += item.data_lead.minat.describe.proses.total;
        dataForChart.data_lead.minat.describe.tidak_pesan.total += item.data_lead.minat.describe.tidak_pesan.total;

        // Aggregate "Tidak Minat" reasons
        item.summary_reasons?.tidak_minat?.forEach(reason => {
          reasonMapTidakMinat.set(reason.reason, (reasonMapTidakMinat.get(reason.reason) || 0) + reason.total);
        });

        // Aggregate "Minat Tidak Pesan" reasons
        item.summary_reasons?.minat_tidak_pesan?.forEach(reason => {
          reasonMapTidakPesan.set(reason.reason, (reasonMapTidakPesan.get(reason.reason) || 0) + reason.total);
        });
      });

      tidakMinatReasons = Array.from(reasonMapTidakMinat, ([reason, total]) => ({ reason, total, persentase: 0 })).sort((a, b) => b.total - a.total);
      tidakPesanReasons = Array.from(reasonMapTidakPesan, ([reason, total]) => ({ reason, total, persentase: 0 })).sort((a, b) => b.total - a.total);
    }
    
    setChartDisplayData(dataForChart);
    setAlasanTidakMinat(tidakMinatReasons);
    setAlasanTidakPesan(tidakPesanReasons);

  }, [rawReportData, filters.property_area_id, isManager, propertyAreas]);


  const chartOptions = useMemo(() => {
    if (!chartDisplayData || !chartDisplayData.data_lead) return null;

    const leadData = chartDisplayData.data_lead;
    const totalCallIn = leadData.call_in?.total || 0;
    if (totalCallIn === 0) return null;

    const sunburstData = [
      { id: 'root', parent: '', name: 'Call In', value: totalCallIn, color: '#1890ff' },
      { id: 'minat', parent: 'root', name: 'Minat', value: leadData.minat.total, color: '#7BAA18' },
      { id: 'tidak_minat', parent: 'root', name: 'Tidak Minat', value: leadData.tidak_minat.total, color: '#ea2e2bff' },
      { id: 'minat_pesan', parent: 'minat', name: 'Pesan', value: leadData.minat.describe.pesan.total, color: '#b5cd49ff' },
      { id: 'minat_proses', parent: 'minat', name: 'Proses', value: leadData.minat.describe.proses.total, color: '#f5df17ff' },
      { id: 'minat_tidak_pesan', parent: 'minat', name: 'Tidak Pesan', value: leadData.minat.describe.tidak_pesan.total, color: '#ffa200ff' },
    ];

    return {
      chart: { height: '500px' },
      title: { text: 'Evaluasi Minat Leads', align: 'left' },
      subtitle: { text: `Berdasarkan total ${totalCallIn} leads (Call In)`, align: 'left' },
      tooltip: {
        useHTML: true,
        headerFormat: '',
        pointFormatter: function (this: Highcharts.Point) {
          const point = this as any;
          const totalValue = point.series.tree.val; // Use the root value for percentage calculation
          const percentage = totalValue ? (point.value / totalValue) * 100 : 0;
          return `<b>${point.name}</b>: ${point.value} Leads (${percentage.toFixed(1)}%)`;
        }
      },
      series: [{
        type: 'sunburst',
        name: 'Leads',
        data: sunburstData.filter(d => d.value > 0), // Filter out zero-value items
        allowDrillToNode: true,
        cursor: 'pointer',
        levels: [{
          level: 1,
          levelIsConstant: false,
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b><br>{point.value}',
            style: { fontSize: '14px', textOutline: 'none', color: 'white' }
          }
        }, {
          level: 2,
          colorByPoint: true,
          dataLabels: {
            enabled: true,
            rotationMode: 'parallel',
            filter: { property: 'outerArcLength', operator: '>', value: 64 },
            format: '<b>{point.name}</b><br>{point.value}',
            style: { fontSize: '12px', textOutline: 'none' }
          }
        }, {
          level: 3,
          colorVariation: { key: 'brightness', to: -0.5 },
          dataLabels: {
            enabled: true,
            rotationMode: 'parallel',
            filter: { property: 'innerArcLength', operator: '>', value: 16 },
            format: '{point.name}<br>{point.value}',
            style: { fontSize: '11px', textOutline: 'none' }
          }
        }]
      }]
    };
  }, [chartDisplayData]);

  const alasanColumns = [
    { title: 'Alasan', dataIndex: 'reason', key: 'reason' },
    { title: 'Jumlah', dataIndex: 'total', key: 'total', width: 100, align: 'center' as const },
  ];

  return (
    <Card>
      <Title level={3}>Evaluasi Leads</Title>
      <Form form={form} layout="inline" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col>
            <DatePicker.RangePicker
              value={filters.date_range}
              onChange={(dates) => setFilters(prev => ({ ...prev, date_range: dates as any }))}
              style={{ width: '100%' }}
            />
          </Col>
          {isManager && (
            <Col>
              <Select
                placeholder="Filter Property Area"
                style={{ width: 200 }}
                value={filters.property_area_id}
                onChange={(value) => setFilters(prev => ({ ...prev, property_area_id: value }))}
                allowClear
              >
                {propertyAreas.map(area => <Option key={area.id} value={area.id}>{area.name}</Option>)}
              </Select>
            </Col>
          )}
        </Row>
      </Form>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        chartOptions ? (
          <>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
            <Divider />
            <Row gutter={[32, 16]}>
              <Col xs={24} md={12}>
                <Title level={4}>Alasan Tidak Minat</Title>
                <Table columns={alasanColumns} dataSource={alasanTidakMinat.map((r, i) => ({ ...r, key: i }))} pagination={false} size="small" bordered />
              </Col>
              <Col xs={24} md={12}>
                <Title level={4}>Alasan Tidak Pesan</Title>
                <Table columns={alasanColumns} dataSource={alasanTidakPesan.map((r, i) => ({ ...r, key: i }))} pagination={false} size="small" bordered />
              </Col>
            </Row>
          </>
        ) : (
          <Empty description="Tidak ada data untuk ditampilkan berdasarkan filter yang dipilih." />
        )
      )}
    </Card>
  );
};

export default EvaluasiPage;
